import { Button, Form, Message, Modal } from '@mairistem/composants';
import type { Bordereau, Exercice, Mouvement } from '@mairistem/comptabilite';
import { isNil } from 'lodash';
import React from 'react';
import type { AnnulationRejetData } from '../../../../../classes/mouvements/liquidation/actions/MouvementAnnulerRejeterAction';
import CODE_BHM from '../../../../../constants/mouvements/codeBHM';
import { TYPE_BORDEREAU } from '../../../../../constants/mouvements/typeBordereau';
import type { BordereauWithState } from '../../../../../interfaces/pieces/TreeRow';
import BordereauModal from '../../../../Mouvement/FichePanel/InformationsGenerales/BordereauModal';
import BordereauSelect from '../../Bordereau/BordereauSelect';

interface MouvementAnnulationRejetModalProps {
	/** Rejet (bordereau de rejet RE) plutôt qu'annulation (bordereau d'annulation 02). */
	estRejet: boolean;
	mouvement: Partial<Mouvement>;
	exercice: Exercice;
	visible: boolean;
	/** Vrai si l'annulation ou le rejet a réussi : sinon, la modale reste ouverte (erreur déjà affichée). */
	onValidate: (data: AnnulationRejetData) => Promise<boolean | undefined>;
	onClose: VoidFunction;
}

/**
 * Annulation ou rejet d'une pièce numérotée : choix (ou création) du bordereau de destination en brouillard, du
 * type voulu, et option de ré-engagement ; la pièce d'annulation ou de rejet reprend le reste à annuler de chaque
 * ligne (MouvementAnnulerRejeterAction).
 */
const MouvementAnnulationRejetModal = ({
	estRejet,
	mouvement,
	exercice,
	visible,
	onValidate,
	onClose,
}: MouvementAnnulationRejetModalProps) => {
	const typeBordereau = estRejet
		? TYPE_BORDEREAU.REJET
		: TYPE_BORDEREAU.DEPENSE.ANNUL_REDUC;
	const [bordereauDestination, setBordereauDestination] =
		React.useState<BordereauWithState | null>(null);
	const [bordereauCree, setBordereauCree] = React.useState<Bordereau | null>(
		null,
	);
	const [reengager, setReengager] = React.useState(false);
	const [isCreationBordereau, setIsCreationBordereau] = React.useState(false);
	const [isEnCours, setIsEnCours] = React.useState(false);
	const [erreur, setErreur] = React.useState<string | null>(null);
	// promesse de BordereauSelect, résolue avec le bordereau créé dans BordereauModal (ou null s'il est fermé)
	const resoudreCreation = React.useRef<
		((bordereau: Bordereau | null) => void) | null
	>(null);

	const handleCreerBordereau = (): Promise<Bordereau> =>
		new Promise((resolve) => {
			resoudreCreation.current = resolve;
			setIsCreationBordereau(true);
		});

	// BordereauModal se ferme avec le bordereau créé, sans rien (erreur) ou avec l'événement de sa croix
	const handleFermerCreation = (fermeture?: unknown): void => {
		const cree =
			fermeture && typeof fermeture === 'object' && 'identifiant' in fermeture
				? (fermeture as Bordereau)
				: null;
		setIsCreationBordereau(false);
		resoudreCreation.current?.(cree);
		resoudreCreation.current = null;
	};

	const handleChangeBordereau = (bordereau: Bordereau): void => {
		setBordereauDestination(bordereau as BordereauWithState);
		setErreur(null);
	};

	const handleValider = async (): Promise<void> => {
		if (isNil(bordereauDestination?.identifiant)) {
			setErreur('Choisissez ou créez le bordereau de destination.');
			return;
		}

		setIsEnCours(true);
		try {
			const isReussi = await onValidate({
				identifiantBordereauDestination:
					bordereauDestination.identifiant as number,
				reengager,
				...(bordereauCree?.identifiant === bordereauDestination.identifiant && {
					bordereauCree,
				}),
			});
			// en cas d'échec, la sélection (et le bordereau créé) est gardée pour un nouvel essai
			if (isReussi !== false) {
				onClose();
			}
		} finally {
			setIsEnCours(false);
		}
	};

	return (
		<>
			<Modal
				closeIcon
				open={visible}
				onClose={onClose}
				data-testid="mouvementAnnulationRejetModal"
			>
				<Modal.Header>
					{estRejet ? 'Rejeter' : 'Annuler'} la pièce n°{mouvement?.numero}
				</Modal.Header>
				<Modal.Content>
					<p>
						{estRejet
							? 'La pièce de rejet reprend, ligne par ligne, le montant qui reste à annuler. Le bordereau de rejet est interne : il n’est pas transmis au comptable.'
							: 'La pièce d’annulation reprend, ligne par ligne, le montant qui reste à annuler. Elle sera transmise au comptable avec son bordereau d’annulation-réduction.'}
					</p>
					<Form error={!isNil(erreur)}>
						<BordereauSelect
							value={bordereauDestination?.identifiant}
							required
							identifiant="new"
							mouvement={mouvement as Mouvement}
							typePes={typeBordereau}
							onChange={handleChangeBordereau}
							onCreateBordereau={handleCreerBordereau}
							label={
								estRejet
									? 'Bordereau de rejet (brouillard)'
									: 'Bordereau d’annulation-réduction (brouillard)'
							}
							data-testid="bordereauDestinationAnnulationRejet"
						/>
						<Form.Checkbox
							label="Ré-engager : le montant annulé redevient disponible sur l’engagement ou la facture repris"
							checked={reengager}
							onChange={() => setReengager((ancien) => !ancien)}
							data-testid="reengagerCheckbox"
						/>
						<Message error content={erreur} />
					</Form>
				</Modal.Content>
				<Modal.Actions>
					<Button
						onClick={onClose}
						disabled={isEnCours}
						data-testid="annulationRejetModalCancel"
					>
						Annuler
					</Button>
					<Button
						primary
						onClick={handleValider}
						loading={isEnCours}
						disabled={isEnCours}
						data-testid="annulationRejetModalConfirm"
					>
						Confirmer
					</Button>
				</Modal.Actions>
			</Modal>
			{isCreationBordereau && (
				<BordereauModal
					open
					isCreation
					bordereau={{
						codeBHM: CODE_BHM.VALUE.BROUILLARD,
						sens: mouvement?.sens,
						typePes: typeBordereau,
					}}
					exercice={exercice}
					entite={exercice?.entite}
					sensDisabled
					typeDisabled
					onConfirm={(bordereau) => setBordereauCree(bordereau as Bordereau)}
					onClose={handleFermerCreation}
				/>
			)}
		</>
	);
};

export default MouvementAnnulationRejetModal;
